import React from 'react';
import { motion } from 'framer-motion';
import Button from './ui/Button';

const images = [
  {
    url: "https://images-pw.pixieset.com/elementfield/K5PRwQe/160orangecountyweddingphotos-7b26bb3b-1500.JPG",
    alt: "Bride and groom kissing in the olive grove at Padua Weddings",
    caption: "A romantic olive grove moment at Padua Weddings."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/6GYLLro/0648-dd0762c0-1000.jpg",
    alt: "Golden hour wedding portrait at Padua Weddings in Claremont",
    caption: "Golden hour portraits framed by the warmth of the foothills."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/6GYLLro/0649-2fb0e033-1000.jpg",
    alt: "Romantic sunset portrait at Padua Weddings olive grove",
    caption: "A quiet sunset moment surrounded by natural beauty."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/lypG6YV/13-5b2cb5dc-1000.jpg",
    alt: "Outdoor ceremony terrace with mountain views at Padua Weddings",
    caption: "An outdoor ceremony setting with foothill views."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/lypG6YV/0888-efca2a4c-1000.jpg",
    alt: "Romantic ceremony aisle with floral arch at Padua Weddings",
    caption: "Walk the space where your celebration comes to life."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/6GYLLro/0427-0acc3790-1000.jpg",
    alt: "Padua Weddings exterior with string lights at night",
    caption: "After sunset, the venue takes on a warm, cinematic glow."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/6GYLLro/0876-440c24a8-1000.jpg",
    alt: "First dance reception moment at Padua Weddings",
    caption: "A reception experience designed for celebration."
  },
  {
    url: "https://images-pw.pixieset.com/elementfield/lypG6YV/33-cdec78f9-1000.jpg",
    alt: "Cinematic night wedding portrait at Padua Weddings",
    caption: "Romantic night portraits with the atmosphere of Padua."
  }
];

const Gallery = () => {
  return (
    <section className="py-24 bg-vintage-white border-t border-warm-stone/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif text-deep-charcoal mb-6">
            A Setting Couples Remember
          </h2>
          <p className="text-lg text-deep-charcoal/70 font-light leading-relaxed">
            From olive grove portraits and open-air ceremonies to warm reception moments and after-dark ambiance, Padua Weddings is designed to feel romantic in person and unforgettable in photographs.
          </p>
        </div>

        {/* Editorial Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
          {/* Featured Large Image - HOVER FIX APPLIED: Removed dark overlay filter */}
          <div className="lg:col-span-7 aspect-[4/5] lg:aspect-auto rounded-2xl overflow-hidden shadow-editorial border border-warm-stone/30 group relative">
            <img 
              src={images[3].url} 
              alt={images[3].alt} 
              loading="lazy" 
              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
            />
            {/* Subtle attribution instead of heavy hover overlay */}
            <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              <span className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-deep-charcoal font-medium">
                Ceremony Terrace
              </span>
            </div>
          </div>

          {/* 2x2 Grid Beside Featured */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-6">
            {images.slice(1, 3).concat(images.slice(5, 7)).map((img, i) => (
              <div key={i} className="aspect-square rounded-xl overflow-hidden shadow-sm border border-warm-stone/30 group">
                <img 
                  src={img.url} 
                  alt={img.alt} 
                  loading="lazy" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {[images[0], images[4], images[7]].map((img, i) => (
            <div key={i} className="flex flex-col group">
              <div className="aspect-video rounded-xl overflow-hidden shadow-sm border border-warm-stone/30 mb-4">
                <img 
                  src={img.url} 
                  alt={img.alt} 
                  loading="lazy" 
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
                />
              </div>
              <p className="text-xs text-deep-charcoal/50 italic font-light px-2">
                {img.caption}
              </p>
            </div>
          ))}
        </div>

        {/* AI SEO Summary Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-aged-ivory p-8 md:p-12 rounded-2xl border border-warm-stone/40 shadow-editorial text-center max-w-4xl mx-auto"
        >
          <h3 className="text-sm font-bold uppercase tracking-widest text-olive-grove mb-4">Visual Summary</h3>
          <p className="text-lg md:text-xl text-deep-charcoal/80 leading-relaxed font-light mb-8">
            Padua Weddings offers a romantic Southern California wedding setting with Spanish Revival architecture, outdoor ceremony spaces, olive grove portraits, warm reception ambiance, in-house catering, coordination support, DJ services, and a private tour experience designed to help couples picture their wedding day clearly.
          </p>
          <Button href="https://tour.paduaweddings.com" variant="primary">
            Schedule Your Private Tour
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default Gallery;